/**
 * Grid Item Component
 * 
 * This component provides a consistent grid item for use within grid layouts.
 * It wraps Material UI's Grid component with additional styling and animation.
 */

import React from 'react';
import { Grid, Paper } from '@mui/material';
import PropTypes from 'prop-types';
import { styled } from '@mui/system';
import { fadeIn } from '../../utils/animations';

// Styled paper component with animation and hover effects and glassmorphism
const StyledPaper = styled(Paper)(({ theme, elevation, animated }) => ({
  padding: theme.spacing(3),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  // Enhanced glassmorphism
  background: 'rgba(40, 40, 40, 0.5)',
  backdropFilter: 'blur(10px) saturate(180%)',
  WebkitBackdropFilter: 'blur(10px) saturate(180%)',
  border: '1px solid rgba(235, 219, 178, 0.08)',
  borderRadius: '8px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: animated ? `${fadeIn} 0.8s ease-out` : 'none',
  ...(elevation > 0 && {
    '&:hover': {
      transform: 'translateY(-5px) scale(1.02)',
      boxShadow: '0 15px 30px rgba(0,0,0,0.25), 0 10px 20px rgba(0,0,0,0.22)',
      background: 'rgba(40, 40, 40, 0.65)',
      backdropFilter: 'blur(14px) saturate(200%)',
      WebkitBackdropFilter: 'blur(14px) saturate(200%)',
      borderColor: 'rgba(235, 219, 178, 0.12)',
    }
  })
}));

/**
 * GridItem component that provides a consistent grid item with paper container
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Grid item content
 * @param {Object} props.xs - Grid item width on extra small screens
 * @param {Object} props.sm - Grid item width on small screens
 * @param {Object} props.md - Grid item width on medium screens
 * @param {Object} props.lg - Grid item width on large screens
 * @param {Object} props.xl - Grid item width on extra large screens
 * @param {number} props.elevation - Paper elevation (shadow depth)
 * @param {boolean} props.animated - Whether to animate the grid item
 * @param {boolean} props.paper - Whether to wrap content in a Paper component
 * @returns {React.ReactElement} Rendered component
 */
const GridItem = ({ 
  children, 
  xs = 12, 
  sm, 
  md, 
  lg, 
  xl,
  elevation = 1,
  animated = true,
  paper = true,
  ...props
}) => {
  return (
    <Grid item xs={xs} sm={sm} md={md} lg={lg} xl={xl} {...props}>
      {paper ? (
        <StyledPaper elevation={elevation} animated={animated}>
          {children}
        </StyledPaper>
      ) : (
        children
      )}
    </Grid>
  );
};

GridItem.propTypes = {
  children: PropTypes.node.isRequired,
  xs: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  sm: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  md: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  lg: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  xl: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  elevation: PropTypes.number,
  animated: PropTypes.bool,
  paper: PropTypes.bool
};

export default GridItem;
